import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faLayerGroup,
  faBoltLightning,
  faUsers,
  faChartBar,
  faBinBottles,
  faClock,
  faBoxes,
  faReact,
  faNodeJs,
  faAws,
} from '@fortawesome/pro-solid-svg-icons';
import { faReact as faReactBrand } from '@fortawesome/free-brands-svg-icons';
import { faArrowRight, faChevronDown, faTowerBroadcast } from '@fortawesome/pro-solid-svg-icons';

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth' });
};

function HeroSection() {
  return (
    <section className="hs-hero" id="hero">
      <div className="container">
        <div className="row align-items-center g-5">
          <div className="col-lg-6 hero-content">
            <span className="hero-eyebrow">Agricultural Operations Technology</span>
            <h1>
              Operation Management <span className="accent">Solutions</span>
            </h1>
            <p className="hero-lead">
              Bespoke cloud-based IT infrastructure to manage fleets and agricultural
              operations. Real-time field data, mobile apps, and browser dashboards
              built for the way you work.
            </p>
            <div className="hero-actions">
              <button className="btn-hs-primary" onClick={() => scrollTo('solutions')}>
                Explore Solutions
              </button>
              <button className="btn-hs-outline" onClick={() => scrollTo('contact')}>
                Get in Touch
              </button>
            </div>
          </div>

          <div className="col-lg-6">
            <DashboardMock />
          </div>
        </div>

        <div className="text-center mt-5 pt-2">
          <button
            onClick={() => scrollTo('services')}
            className="btn btn-link"
            style={{ color: 'rgba(255,255,255,0.35)', fontSize: '1.2rem' }}
            aria-label="Scroll down"
          >
            <FontAwesomeIcon icon={faChevronDown} className="fa-bounce" />
          </button>
        </div>
      </div>
    </section>
  );
}

function DashboardMock() {
  return (
    <div className="hs-dashboard-mock">
      <div className="mock-titlebar">
        <span className="dot red" />
        <span className="dot amber" />
        <span className="dot green" />
        <span className="mock-title">Headstation - BinTracker</span>
      </div>

      <div className="mock-metrics">
        <div className="mock-metric">
          <div className="metric-value">12</div>
          <div className="metric-label">Vehicles</div>
        </div>
        <div className="mock-metric">
          <div className="metric-value">94%</div>
          <div className="metric-label">On Schedule</div>
        </div>
        <div className="mock-metric">
          <div className="metric-value">4.2t</div>
          <div className="metric-label">Harvested</div>
        </div>
      </div>

      <div className="mock-modules">
        <div className="mock-module">
          <span className="module-name">Cane Cutter Logger Tracking</span>
          <span className="module-badge active">Active</span>
        </div>
        <div className="mock-module">
          <span className="module-name">Live Field Map</span>
          <span className="module-badge live">Live</span>
        </div>
        <div className="mock-module">
          <span className="module-name">BinTracker — 3 Consignments</span>
          <span className="module-badge active">Active</span>
        </div>
        <div className="mock-module">
          <span className="module-name">Customer Portal</span>
          <span className="module-badge active">Active</span>
        </div>
      </div>
    </div>
  );
}

function ServicesSection() {
  const capabilities = [
    {
      icon: faLayerGroup,
      title: 'Multi-App Platform',
      desc: 'A multi-tenanted, multi-app framework that processes data from mobile apps and field loggers, visualising information through browser interfaces tailored to each role.',
    },
    {
      icon: faBoltLightning,
      title: 'Real-Time Field Data',
      desc: 'Live telemetry from field loggers and mobile devices delivered to browser-based dashboards, giving operations teams an up-to-the-minute view of every asset.',
    },
    {
      icon: faUsers,
      title: 'Customer Portals',
      desc: 'Branded, role-appropriate interfaces for growers, contractors, and management — each seeing exactly the data they need, secured and scoped to their organisation.',
    },
    {
      icon: faChartBar,
      title: 'PowerBI & QGIS Integration',
      desc: 'Structured outputs compatible with industry-standard analytics and GIS tools, enabling deeper reporting and spatial analysis on your operational data.',
    },
  ];

  return (
    <section className="hs-section hs-what-we-do" id="services">
      <div className="container">
        <div className="row g-5 align-items-center">
          <div className="col-lg-4">
            <p className="section-eyebrow">What We Do</p>
            <h2 className="section-title">
              Purpose-built infrastructure for complex operations
            </h2>
            <p className="section-lead">
              Headstation creates operations management solutions and cloud-based IT
              infrastructure. Our applications process data from mobile apps and field
              loggers and visualise it through powerful browser interfaces.
            </p>
            <button className="btn-hs-primary mt-3" onClick={() => scrollTo('solutions')}>
              View Solutions <FontAwesomeIcon icon={faArrowRight} className="ms-1" />
            </button>
          </div>

          <div className="col-lg-8">
            <div className="row g-3">
              {capabilities.map((cap) => (
                <div className="col-sm-6" key={cap.title}>
                  <div className="capability-card">
                    <div className="capability-icon">
                      <FontAwesomeIcon icon={cap.icon} />
                    </div>
                    <h4>{cap.title}</h4>
                    <p>{cap.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function SolutionsSection() {
  const solutions = [
    {
      icon: faBinBottles,
      title: 'BinTracker',
      status: 'available',
      statusLabel: '',
      desc: 'Sugar cane consignment management with geofencing and mobile tracking. Track bin movements from paddock to mill in real time.',
      features: [
        'Real-time GPS bin tracking',
        'Geofence arrival / departure alerts',
        'Mobile app for drivers and operators',
        'Mill integration and consignment reporting',
      ],
      featured: true,
    },
    {
      icon: faClock,
      title: 'Timekeeper',
      status: 'coming-soon',
      statusLabel: '',
      desc: 'Fleet and workforce hour and shift management. Capture time-on-machine, manage rosters, and automate payroll-ready reports.',
      features: [
        'Shift and roster management',
        'Machine hours tracking',
        'Payroll-ready export',
        'GPS-validated clock-in/out',
      ],
      featured: false,
    },
    {
      icon: faBoxes,
      title: 'Resources',
      status: 'coming-soon',
      statusLabel: '',
      desc: 'Equipment and asset tracking across your entire operation. Service schedules, fault reporting, and utilisation dashboards.',
      features: [
        'Asset register and lifecycle tracking',
        'Service schedule reminders',
        'Fault and breakdown reporting',
        'Utilisation and cost analytics',
      ],
      featured: false,
    },
  ];

  return (
    <section className="hs-section hs-solutions" id="solutions">
      <div className="container">
        <div className="text-center mb-5">
          <p className="section-eyebrow">Solutions</p>
          <h2 className="section-title mx-auto" style={{ maxWidth: 520 }}>
            Modular apps built for agriculture
          </h2>
          <p className="section-lead mx-auto">
            Each solution is designed to integrate with the others, giving you a
            single platform that grows with your operation.
          </p>
        </div>

        <div className="row g-4 justify-content-center">
          {solutions.map((sol) => (
            <div className="col-md-6 col-lg-4" key={sol.title}>
              <div className={`solution-card ${sol.featured ? 'featured' : ''}`}>
                <div className="solution-icon">
                  <FontAwesomeIcon icon={sol.icon} />
                </div>
                <h3>{sol.title}</h3>
                <span className={`solution-status ${sol.status}`}>{sol.statusLabel}</span>
                <p>{sol.desc}</p>
                <ul className="solution-features">
                  {sol.features.map((f) => (
                    <li key={f}>
                      <span className="feature-dot" />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function TechnologySection() {
  const stack = [
    {
      category: 'Frontend',
      name: 'React SPAs',
      desc: 'Single-page applications with real-time data and responsive interfaces.',
    },
    {
      category: 'Mobile',
      name: 'React Native',
      desc: 'Cross-platform mobile apps for drivers, operators and supervisors.',
    },
    {
      category: 'Backend',
      name: 'Node.js Microservices',
      desc: 'Event-driven APIs and background workers processing field data at scale.',
    },
    {
      category: 'Database',
      name: 'PostgreSQL + PostGIS',
      desc: 'Relational data with full spatial capabilities for geofencing and mapping.',
    },
    {
      category: 'Infrastructure',
      name: 'AWS + Kubernetes',
      desc: 'S3, CloudFront, ECS/EKS, and GitLab CI/CD for reliable continuous delivery.',
    },
    {
      category: 'Analytics',
      name: 'Python / R Services',
      desc: 'Statistical microservices providing insights into operational performance.',
    },
    {
      category: 'Visualisation',
      name: 'PowerBI & QGIS',
      desc: 'Industry-standard tools for management reporting and spatial analysis.',
    },
    {
      category: 'Field Devices',
      name: 'Custom Loggers',
      desc: 'Rugged field hardware transmitting GPS, sensor, and event data in real time.',
    },
  ];

  return (
    <section className="hs-section hs-technology" id="technology">
      <div className="container">
        <div className="row align-items-start g-5">
          <div className="col-lg-4">
            <p className="section-eyebrow" style={{ color: '#00b4d8' }}>Technology</p>
            <h2 className="section-title" style={{ color: '#fff' }}>
              Built on proven, modern infrastructure
            </h2>
            <p className="section-lead" style={{ color: 'rgba(255,255,255,0.6)' }}>
              Every component of our stack is chosen for reliability, scalability,
              and ease of integration with the broader agricultural technology ecosystem.
            </p>
          </div>

          <div className="col-lg-8">
            <div className="tech-stack-grid">
              {stack.map((t) => (
                <div className="tech-item" key={t.name}>
                  <p className="tech-category">{t.category}</p>
                  <p className="tech-name">{t.name}</p>
                  <p className="tech-desc">{t.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function CTASection() {
  return (
    <section className="hs-section hs-cta" id="contact">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-8 text-center">
            <span style={{
              display: 'inline-block',
              background: 'rgba(255,255,255,0.15)',
              border: '1px solid rgba(255,255,255,0.25)',
              color: 'rgba(255,255,255,0.9)',
              fontSize: '0.75rem',
              fontWeight: 600,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              padding: '0.35rem 0.9rem',
              borderRadius: '100px',
              marginBottom: '1.5rem',
            }}>
              Get in Touch
            </span>
            <h2>Ready to streamline your operation?</h2>
            <p>
              Tell us about your operation and we'll show you how Headstation can
              save time, reduce errors, and give you the visibility you need.
            </p>
            <a target="_blank" href="https://www.linkedin.com/company/headstation-australia" className="btn-hs-white">
              Contact us on Linkedin
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <HeroSection />
      <ServicesSection />
      <SolutionsSection />
      <TechnologySection />
      <CTASection />
    </>
  );
}
